import { yupResolver } from "@hookform/resolvers/yup";
import { useQueryClient } from "@tanstack/react-query";
import { t } from "i18next";
import type React from "react";
import { useEffect, useMemo, useState } from "react";
import type { SubmitHandler } from "react-hook-form";
import { useForm } from "react-hook-form";
import * as yup from "yup";

import { getDataFromResponse } from "~/api/axiosUtils.ts";
import Button from "~/components/buttons/Button/Button.tsx";
import FormHasErrorsHint from "~/components/formElements/FormHasErrorsHint";
import FormSection, { FormSectionGroup } from "~/components/formElements/FormSection";
import Input from "~/components/formElements/Input";
import SubmitButton from "~/components/formElements/SubmitButton";
import AccountingDetailsFormSection from "~/components/formSections/AccountingDetailsFormSection";
import AddressFormSection from "~/components/formSections/AddressFormSection/AddressFormSection.tsx";
import CommentFormSection from "~/components/formSections/CommentFormSection";
import SidebarBusyOverlay from "~/components/Sidebar/components/SidebarBusyOverlay";
import SidebarContent from "~/components/Sidebar/components/SidebarContent";
import SidebarFooter from "~/components/Sidebar/components/SidebarFooter";
import SidebarHeader from "~/components/Sidebar/components/SidebarHeader";
import SidebarHeaderHeadline from "~/components/Sidebar/components/SidebarHeaderHeadline";
import { DEFAULT_COUNTRY_CODE } from "~/constants/form.ts";
import useCountryCodeSelectOptions from "~/hooks/form/formOptionsData/useCountryCodeSelectOptions.ts";
import { useFormIsSubmittable } from "~/hooks/form/useFormIsSubmittable.ts";
import {
	useCompanyValidationComparisonData,
} from "~/hooks/form/validationComparisonData/useCompanyValidationComparisonData.ts";
import { ALL_CLIENTS_QUERY_KEY } from "~/modules/client/api/client/clientQueries.ts";
import { createCompany } from "~/modules/client/api/company/companyApiDispatchers.ts";
import { All_COMPANIES_QUERY_KEY } from "~/modules/client/api/company/companyQueries.ts";
import type { CreateCompanyData } from "~/modules/client/api/company/companyTypes.ts";
import { normalizeTKey } from "~/types/typeHelpers.ts";
import { preventSubmitOnEnter } from "~/utils/form/formUtils.ts";

type Props = {
	onSuccess: (newCompanyId: string) => void;
	onCancel: () => void;
};

interface CreateCompanyFormData extends Omit<CreateCompanyData, "paymentTargetDays" | "avatarImage" | "address"> {
	addressLine1: string | null;
	addressLine2: string | null;
	addressLine3: string | null;
	city: string | null;
	postCode: string | null;
	countryCode: string | null;
	paymentTargetDays: number;
	paymentTargetDaysCustom?: number | null;
}

const CreateCompanyForm: React.FC<Props> = ({ onSuccess, onCancel }) => {
	const [busy, setBusy] = useState(false);
	const queryClient = useQueryClient();

	const { displayNameComparisonData, shortNameComparisonData, corporateNameComparisonData } =
		useCompanyValidationComparisonData();
	const schema = useMemo(() => {
		return yup.object({
			addressLine1: yup.string().default(""),
			addressLine2: yup.string().default(""),
			addressLine3: yup.string().default(""),
			billingViaThirdParty: yup.boolean().required(),
			city: yup.string().default(""),
			comment: yup.string().default(""),
			corporateName: yup.string().required().unique(corporateNameComparisonData),
			countryCode: yup.string().default(""),
			displayName: yup.string().required().unique(displayNameComparisonData),
			invoiceByMail: yup.boolean().required(),
			isVatExempt: yup.boolean().required(),
			paymentTargetDays: yup.number().required(),
			paymentTargetDaysCustom: yup.number()
				.nullable()
				.when("paymentTargetDays", (field1Value: number[], schema) => {
					return field1Value[0] === 0
						? schema.required()
						: schema.nullable();
				}),
			postCode: yup.string().default(""),
			shortName: yup.string().required().unique(shortNameComparisonData),
			travelExpensesIncluded: yup.boolean().required(),
		});
	}, [corporateNameComparisonData, displayNameComparisonData, shortNameComparisonData]);

	const defaultValues = useMemo(() => {
		return {
			addressLine1: "",
			addressLine2: "",
			addressLine3: "",
			billingViaThirdParty: false,
			city: "",
			comment: "",
			corporateName: "",
			countryCode: DEFAULT_COUNTRY_CODE,
			displayName: "",
			invoiceByMail: true,
			isVatExempt: false,
			paymentTargetDays: 30,
			paymentTargetDaysCustom: null,
			postCode: "",
			shortName: "",
			travelExpensesIncluded: true,
		};
	}, []);

	const {
		handleSubmit,
		control,
		watch,
		formState: { isValid, isDirty, isSubmitted },
		trigger,
	} = useForm<CreateCompanyFormData>({
		defaultValues: defaultValues,
		resolver: yupResolver<CreateCompanyFormData>(schema),
	});

	const formIsSubmittable = useFormIsSubmittable({ isDirty, isSubmitted, isValid, isLoading: busy });

	const showCustomPaymentTargetInput = watch("paymentTargetDays") === 0;

	const countryCodeSelectOptions = useCountryCodeSelectOptions();

	useEffect(() => {
		if (showCustomPaymentTargetInput) {
			trigger("paymentTargetDaysCustom");
		}
	}, [showCustomPaymentTargetInput, trigger]);

	const onSubmit: SubmitHandler<CreateCompanyFormData> = async (data) => {
		setBusy(true);
		const { paymentTargetDays, paymentTargetDaysCustom, addressLine1, addressLine2, addressLine3, city, postCode, countryCode, ...rest } = data;
		const paymentTargetDaysValue =
			paymentTargetDays === 0 && paymentTargetDaysCustom ? paymentTargetDaysCustom : paymentTargetDays;

		const hasAddress = addressLine1 || addressLine2 || addressLine3 || city || postCode || countryCode;
		const address = hasAddress ? { addressLine1, addressLine2, addressLine3, city, postCode, countryCode } : null;

		const newCompanyData: CreateCompanyData = { paymentTargetDays: paymentTargetDaysValue, address, ...rest };
		try {
			const newCompanyRespone = await createCompany(newCompanyData);
			const companyId = getDataFromResponse(newCompanyRespone).id;
			onSuccess(companyId);
			queryClient.invalidateQueries({ queryKey: All_COMPANIES_QUERY_KEY });
			queryClient.invalidateQueries({ queryKey: ALL_CLIENTS_QUERY_KEY });
		} catch (error) {
			console.log(error);
		}
	};

	return (
		<form
			onSubmit={handleSubmit(onSubmit)}
			onKeyDown={preventSubmitOnEnter}
			className="flex min-h-full w-full flex-col justify-start"
		>
			<SidebarHeader>
				<SidebarHeaderHeadline>Neuen Kunden anlegen</SidebarHeaderHeadline>
			</SidebarHeader>
			<SidebarContent>
				{busy && <SidebarBusyOverlay />}
				<FormSectionGroup>
					<FormSection title="Kunde">
						<div className="grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-6">
							<div className="col-span-full sm:col-start-1">
								<Input
									name="corporateName"
									control={control}
									label="Name Legaleinheit"
									placeholder="NORD.NORD GmbH"
								/>
							</div>
							<div className="col-span-3 sm:col-start-1">
								<Input
									name="displayName"
									control={control}
									label="Kundenname"
									placeholder="NORD.NORD"
								/>
							</div>
							<div className="col-span-3">
								<Input
									name="shortName"
									control={control}
									placeholder="KN"
									label="Kürzel für Projektnummer"
								/>
							</div>
						</div>
					</FormSection>

					<AddressFormSection<CreateCompanyFormData>
						control={control}
						countryCodeSelectOptions={countryCodeSelectOptions}
					/>

					<AccountingDetailsFormSection<CreateCompanyFormData>
						control={control}
						showCustomPaymentTargetInput={showCustomPaymentTargetInput}
					/>
					<CommentFormSection
						control={control}
						label="Rechnungsbesonderheiten"
						placeholder="Hier können allgemeine Anmerkungen für die Rechnungserstellung hinterlegt werden."
						title="Kommentar" />
				</FormSectionGroup>
			</SidebarContent>
			<SidebarFooter>
				<FormHasErrorsHint show={isSubmitted && !isValid}
								   className="mr-2" />
				<SubmitButton busy={busy}
							  disabled={!formIsSubmittable}>
					{t(normalizeTKey("form:buttonTexts.save"))}
				</SubmitButton>
				<Button theme="none"
						onClick={onCancel}>
					{t(normalizeTKey("form:buttonTexts.cancel"))}
				</Button>
			</SidebarFooter>
		</form>
	);
};

export default CreateCompanyForm;
